import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Clock, TrendUp, Users, Tag, Brain, Sparkle, CheckCircle, XCircle, Lightning, ArrowUpRight, Lightbulb, ShieldCheck, Gavel, Trophy, WarningCircle, ChatCircle, Heart, Trash, ArrowBendUpLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function MarketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, refreshUser, user } = useAuth();
  const [market, setMarket] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tradeAmount, setTradeAmount] = useState('');
  const [trading, setTrading] = useState(false);
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  const fetchMarket = useCallback(async () => {
    try {
      const [mRes, iRes, cRes] = await Promise.all([
        api.get(`/markets/${id}`),
        api.get(`/insights/${id}`),
        api.get(`/comments/${id}`)
      ]);
      setMarket(mRes.data);
      if (iRes.data?.insights) setInsights(iRes.data);
      setComments(cRes.data || []);
    } catch (err) {
      toast.error('Failed to load market');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [api, id, navigate]);

  useEffect(() => { fetchMarket(); }, [fetchMarket]);

  const handleTrade = async (position) => {
    const amount = parseFloat(tradeAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setTrading(true);
    try {
      const { data } = await api.post('/trades', { market_id: id, position, amount });
      toast.success(`${position.toUpperCase()} position placed! ${data.shares} shares at $${data.price.toFixed(2)}`);
      setTradeAmount('');
      await refreshUser();
      fetchMarket();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Trade failed');
    } finally {
      setTrading(false);
    }
  };

  const handleGenerateInsights = async () => {
    setGeneratingInsights(true);
    try {
      const { data } = await api.post(`/insights/${id}/generate`);
      setInsights(data);
      toast.success('AI insights generated!');
    } catch (err) {
      toast.error('Failed to generate insights');
    } finally {
      setGeneratingInsights(false);
    }
  };

  const handleResolve = async (resolution) => {
    if (!window.confirm(`Are you sure you want to resolve this market as ${resolution.toUpperCase()}? This will process all payouts and cannot be undone.`)) return;
    setResolving(true);
    try {
      const { data } = await api.post(`/admin/markets/${id}/resolve`, { resolution });
      toast.success(`Market resolved as ${resolution.toUpperCase()}! ${data.winners} winners paid out, ${data.losers} losers.`);
      fetchMarket();
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resolution failed');
    } finally {
      setResolving(false);
    }
  };

  const handleAddComment = async (parentId = null) => {
    const text = parentId ? replyText : commentText;
    if (!text.trim()) return;
    setSubmittingComment(true);
    try {
      await api.post(`/comments/${id}`, { text: text.trim(), parent_id: parentId });
      parentId ? setReplyText('') : setCommentText('');
      setReplyTo(null);
      const { data } = await api.get(`/comments/${id}`);
      setComments(data || []);
    } catch (err) {
      toast.error('Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/comments/${commentId}`);
      const { data } = await api.get(`/comments/${id}`);
      setComments(data || []);
    } catch {
      toast.error('Failed to delete comment');
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      await api.post(`/comments/${commentId}/like`);
      const { data } = await api.get(`/comments/${id}`);
      setComments(data || []);
    } catch {}
  };

  if (loading) return (
    <div className="min-h-screen pt-24 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-oh-green border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!market) return null;

  const yesProb = (market.yes_probability * 100).toFixed(1);
  const noProb = (market.no_probability * 100).toFixed(1);
  const daysLeft = Math.max(0, Math.ceil((new Date(market.closes_at) - new Date()) / (1000 * 60 * 60 * 24)));
  const amount = parseFloat(tradeAmount) || 0;
  const yesShares = amount > 0 ? (amount / market.yes_probability).toFixed(2) : '0';
  const noShares = amount > 0 ? (amount / market.no_probability).toFixed(2) : '0';
  const yesPayout = amount > 0 ? (parseFloat(yesShares) * 1).toFixed(2) : '0';
  const noPayout = amount > 0 ? (parseFloat(noShares) * 1).toFixed(2) : '0';

  const insightData = insights?.insights;

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 pb-12" data-testid="market-detail-page">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button onClick={() => navigate('/markets')} className="flex items-center gap-2 text-[var(--oh-text-secondary)] hover:text-white transition-colors mb-6 mt-4" data-testid="back-button">
          <ArrowLeft size={18} /> Back to Markets
        </button>

        {/* Resolved Banner */}
        {market.status === 'resolved' && (
          <div className={`rounded-xl p-5 mb-6 flex items-center gap-4 border ${market.resolution === 'yes' ? 'bg-[rgba(0,255,163,0.08)] border-[rgba(0,255,163,0.2)]' : 'bg-[rgba(0,184,255,0.08)] border-[rgba(0,184,255,0.2)]'}`} data-testid="resolved-banner">
            <Trophy size={28} weight="fill" className={market.resolution === 'yes' ? 'text-[#00FFA3]' : 'text-[#00B8FF]'} />
            <div>
              <p className="text-white font-heading text-lg">Market Resolved: <span className={market.resolution === 'yes' ? 'text-[#00FFA3]' : 'text-[#00B8FF]'}>{market.resolution?.toUpperCase()}</span></p>
              <p className="text-[var(--oh-text-secondary)] text-sm">This market has been resolved. Payouts have been distributed to winning positions.</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Market Info + AI Insights */}
          <div className="lg:col-span-2 space-y-6">
            {/* Market Header Card */}
            <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-6 md:p-8 animate-fade-up">
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-[rgba(0,255,163,0.1)] text-[#00FFA3] border border-[rgba(0,255,163,0.2)] text-xs uppercase tracking-widest px-2 py-1 rounded-sm flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00FFA3] pulse-dot" /> Live
                </span>
                <span className="text-xs text-[var(--oh-text-muted)] uppercase tracking-wider">{market.category}</span>
                <span className="flex items-center gap-1 text-xs text-[var(--oh-text-secondary)]"><Tag size={12} weight="bold" /> {market.country}</span>
                {market.ai_generated && (
                  <span className="bg-[rgba(0,255,163,0.1)] text-[#00FFA3] text-xs px-2 py-1 rounded-full border border-[rgba(0,255,163,0.2)]">
                    AI Generated
                  </span>
                )}
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[var(--oh-text)] tracking-tight leading-tight mb-4" data-testid="market-question">
                {market.question}
              </h1>
              <p className="text-[var(--oh-text-secondary)] text-base mb-6">{market.description}</p>

              {/* Probability Display */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-[rgba(0,255,163,0.08)] border border-[rgba(0,255,163,0.2)] rounded-xl p-5 text-center">
                  <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#00FFA3] block mb-1">Yes</span>
                  <span className="font-heading text-4xl font-bold text-[#00FFA3]" data-testid="yes-probability">{yesProb}%</span>
                </div>
                <div className="bg-[rgba(0,184,255,0.08)] border border-[rgba(0,184,255,0.2)] rounded-xl p-5 text-center">
                  <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#00B8FF] block mb-1">No</span>
                  <span className="font-heading text-4xl font-bold text-[#00B8FF]" data-testid="no-probability">{noProb}%</span>
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-6 text-sm text-[var(--oh-text-muted)]">
                <div className="flex items-center gap-1.5">
                  <TrendUp size={16} className="text-[#F59E0B]" />
                  <span className="font-mono">${(market.total_volume || 0).toLocaleString()}</span> volume
                </div>
                <div className="flex items-center gap-1.5">
                  <Users size={16} />
                  <span className="font-mono">{market.trade_count || 0}</span> trades
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock size={16} />
                  <span>{daysLeft} days left</span>
                </div>
              </div>
            </div>

            {/* Before You Trade - AI Insights Panel */}
            <div className="ai-panel-border bg-[var(--oh-bg)] border border-[rgba(0,184,255,0.3)] rounded-xl p-6 md:p-8 animate-fade-up" style={{ animationDelay: '0.1s' }} data-testid="ai-insights-panel">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Brain size={24} className="text-[#00B8FF]" weight="fill" />
                  <h2 className="font-heading text-lg sm:text-xl text-[var(--oh-text)]">Before You Trade</h2>
                </div>
                <div className="flex items-center gap-2">
                  <span className="bg-[rgba(0,184,255,0.1)] text-[#00B8FF] text-xs px-2 py-1 rounded-full border border-[rgba(0,184,255,0.2)]">
                    Claude 4.5
                  </span>
                  <span className="bg-[rgba(0,255,163,0.1)] text-[#00FFA3] text-xs px-2 py-1 rounded-full border border-[rgba(0,255,163,0.2)]">
                    GPT-5.2
                  </span>
                </div>
              </div>

              {insightData ? (
                <div className="space-y-6">
                  {/* Key Insights */}
                  <div>
                    <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--oh-text-secondary)] mb-3 flex items-center gap-2">
                      <Lightbulb size={14} className="text-[#F59E0B]" weight="fill" /> Key Insights
                    </h3>
                    <ul className="space-y-2">
                      {insightData.key_insights?.map((insight, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[var(--oh-text-secondary)]">
                          <Lightning size={14} className="text-[#F59E0B] mt-0.5 flex-shrink-0" weight="fill" />
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* YES/NO Factors */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-[rgba(0,255,163,0.05)] rounded-lg p-4 border border-[rgba(0,255,163,0.1)]">
                      <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-[#00FFA3] mb-2 flex items-center gap-2">
                        <CheckCircle size={14} weight="fill" /> Supporting YES
                      </h4>
                      <ul className="space-y-1.5">
                        {insightData.supporting_factors_yes?.map((f, i) => (
                          <li key={i} className="text-xs text-[var(--oh-text-secondary)]">{f}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-[rgba(0,184,255,0.05)] rounded-lg p-4 border border-[rgba(0,184,255,0.1)]">
                      <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-[#00B8FF] mb-2 flex items-center gap-2">
                        <XCircle size={14} weight="fill" /> Supporting NO
                      </h4>
                      <ul className="space-y-1.5">
                        {insightData.supporting_factors_no?.map((f, i) => (
                          <li key={i} className="text-xs text-[var(--oh-text-secondary)]">{f}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Strategy */}
                  <div className="bg-[var(--oh-card)] rounded-lg p-4 border border-[var(--oh-border)]">
                    <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--oh-text-secondary)] mb-2 flex items-center gap-2">
                      <ShieldCheck size={14} className="text-[#00FFA3]" weight="fill" /> Suggested Strategy
                    </h4>
                    <p className="text-sm text-[var(--oh-text)]">{insightData.suggested_strategy}</p>
                    <p className="text-xs text-[var(--oh-text-muted)] mt-2">{insightData.confidence_note}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Sparkle size={32} className="mx-auto mb-3 text-[#00B8FF]" weight="fill" />
                  <p className="text-[var(--oh-text-secondary)] text-sm mb-4">Get AI-powered research and insights before placing your trade</p>
                  <Button onClick={handleGenerateInsights} disabled={generatingInsights}
                    className="bg-gradient-to-r from-[#00FFA3] to-[#00B8FF] text-black font-bold hover:scale-[1.02] transition-transform"
                    data-testid="generate-insights-button">
                    <Brain size={16} className="mr-2" weight="fill" />
                    {generatingInsights ? 'Analyzing...' : 'Generate AI Insights'}
                  </Button>
                </div>
              )}
            </div>

            {/* Comments / Discussion */}
            <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-6 md:p-8 animate-fade-up" style={{ animationDelay: '0.2s' }} data-testid="comments-section">
              <div className="flex items-center gap-2 mb-6">
                <ChatCircle size={22} className="text-[var(--oh-text-secondary)]" weight="fill" />
                <h2 className="font-heading text-lg text-[var(--oh-text)]">Discussion</h2>
                <span className="text-xs font-mono text-[var(--oh-text-muted)] ml-1">({comments.filter(c => !c.parent_id).length})</span>
              </div>

              {/* Comment Input */}
              <div className="mb-6">
                <Textarea value={commentText} onChange={e => setCommentText(e.target.value)}
                  placeholder="Share your analysis or thoughts..."
                  className="bg-[var(--oh-bg)] border-[var(--oh-border)] focus:border-[#00FFA3] focus:ring-1 focus:ring-[#00FFA3] text-[var(--oh-text)] placeholder-[var(--oh-text-muted)] min-h-[80px] resize-none"
                  data-testid="comment-input" />
                <div className="flex justify-end mt-2">
                  <Button onClick={() => handleAddComment(null)} disabled={submittingComment || !commentText.trim()}
                    className="bg-gradient-to-r from-[#00FFA3] to-[#00B8FF] text-black font-bold hover:scale-[1.02] transition-transform text-sm px-5"
                    data-testid="comment-submit-button">
                    {submittingComment ? 'Posting...' : 'Post Comment'}
                  </Button>
                </div>
              </div>

              {/* Comments List */}
              <div className="space-y-4">
                {comments.filter(c => !c.parent_id).map(comment => {
                  const replies = comments.filter(c => c.parent_id === comment.id);
                  const isOwn = user?.id === comment.user_id;
                  const isLiked = comment.liked_by?.includes(user?.id);
                  return (
                    <div key={comment.id} className="group" data-testid={`comment-${comment.id}`}>
                      <div className="flex gap-3">
                        <Link to={`/profile/${comment.user_id}`} className="flex-shrink-0">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00FFA3] to-[#00B8FF] flex items-center justify-center text-black font-bold text-xs">
                            {comment.user_name[0].toUpperCase()}
                          </div>
                        </Link>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Link to={`/profile/${comment.user_id}`} className="text-sm font-medium text-[var(--oh-text)] hover:text-[#00FFA3] transition-colors">
                              {comment.user_name}
                            </Link>
                            <span className="text-xs text-[var(--oh-text-muted)]">{new Date(comment.created_at).toLocaleDateString()}</span>
                          </div>
                          <p className="text-sm text-[var(--oh-text-secondary)] leading-relaxed whitespace-pre-wrap">{comment.text}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <button onClick={() => handleLikeComment(comment.id)}
                              className={`flex items-center gap-1 text-xs transition-colors ${isLiked ? 'text-[#FF3B30]' : 'text-[var(--oh-text-muted)] hover:text-[#FF3B30]'}`}
                              data-testid={`like-${comment.id}`}>
                              <Heart size={14} weight={isLiked ? 'fill' : 'regular'} /> {comment.likes || 0}
                            </button>
                            <button onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
                              className="flex items-center gap-1 text-xs text-[var(--oh-text-muted)] hover:text-[#00B8FF] transition-colors"
                              data-testid={`reply-toggle-${comment.id}`}>
                              <ArrowBendUpLeft size={14} /> Reply
                            </button>
                            {(isOwn || user?.role === 'admin') && (
                              <button onClick={() => handleDeleteComment(comment.id)}
                                className="flex items-center gap-1 text-xs text-[var(--oh-text-muted)] hover:text-[#FF3B30] transition-colors"
                                data-testid={`delete-${comment.id}`}>
                                <Trash size={14} /> Delete
                              </button>
                            )}
                          </div>

                          {/* Reply Input */}
                          {replyTo === comment.id && (
                            <div className="mt-3 flex gap-2">
                              <Input value={replyText} onChange={e => setReplyText(e.target.value)}
                                placeholder="Write a reply..."
                                className="bg-[var(--oh-bg)] border-[var(--oh-border)] focus:border-[#00B8FF] focus:ring-1 focus:ring-[#00B8FF] text-[var(--oh-text)] placeholder-[var(--oh-text-muted)] h-9 text-sm"
                                data-testid={`reply-input-${comment.id}`}
                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(comment.id); } }} />
                              <Button onClick={() => handleAddComment(comment.id)} disabled={submittingComment || !replyText.trim()}
                                className="bg-[#00B8FF] text-black font-bold h-9 px-4 text-xs" data-testid={`reply-submit-${comment.id}`}>
                                Reply
                              </Button>
                            </div>
                          )}

                          {/* Replies */}
                          {replies.length > 0 && (
                            <div className="mt-3 space-y-3 pl-4 border-l border-[var(--oh-border)]">
                              {replies.map(reply => (
                                <div key={reply.id} className="flex gap-3 group/reply" data-testid={`reply-${reply.id}`}>
                                  <Link to={`/profile/${reply.user_id}`} className="flex-shrink-0">
                                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#00B8FF] to-[#00FFA3] flex items-center justify-center text-black font-bold text-[10px]">
                                      {reply.user_name[0].toUpperCase()}
                                    </div>
                                  </Link>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                      <Link to={`/profile/${reply.user_id}`} className="text-xs font-medium text-[var(--oh-text)] hover:text-[#00FFA3] transition-colors">{reply.user_name}</Link>
                                      <span className="text-[10px] text-[var(--oh-text-muted)]">{new Date(reply.created_at).toLocaleDateString()}</span>
                                    </div>
                                    <p className="text-xs text-[var(--oh-text-secondary)] leading-relaxed">{reply.text}</p>
                                    <div className="flex items-center gap-3 mt-1">
                                      <button onClick={() => handleLikeComment(reply.id)}
                                        className={`flex items-center gap-1 text-[10px] transition-colors ${reply.liked_by?.includes(user?.id) ? 'text-[#FF3B30]' : 'text-[var(--oh-text-muted)] hover:text-[#FF3B30]'}`}>
                                        <Heart size={12} weight={reply.liked_by?.includes(user?.id) ? 'fill' : 'regular'} /> {reply.likes || 0}
                                      </button>
                                      {(user?.id === reply.user_id || user?.role === 'admin') && (
                                        <button onClick={() => handleDeleteComment(reply.id)}
                                          className="text-[10px] text-[var(--oh-text-muted)] hover:text-[#FF3B30] transition-colors opacity-0 group-hover/reply:opacity-100">
                                          <Trash size={12} />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {comments.filter(c => !c.parent_id).length === 0 && (
                  <div className="text-center py-8">
                    <ChatCircle size={32} className="mx-auto mb-2 text-[var(--oh-text-muted)]" />
                    <p className="text-sm text-[var(--oh-text-muted)]">No comments yet. Be the first to share your analysis!</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Trade Panel */}
          <div className="space-y-6">
            {market.status === 'resolved' ? (
              /* Resolved Market - No Trading */
              <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-6 sticky top-24 animate-fade-up" style={{ animationDelay: '0.15s' }} data-testid="trade-panel-resolved">
                <div className="flex items-center gap-2 mb-4">
                  <WarningCircle size={20} className="text-[#F59E0B]" weight="fill" />
                  <h3 className="font-heading text-lg text-[var(--oh-text)]">Market Closed</h3>
                </div>
                <div className={`rounded-xl p-5 text-center border ${market.resolution === 'yes' ? 'bg-[rgba(0,255,163,0.08)] border-[rgba(0,255,163,0.2)]' : 'bg-[rgba(0,184,255,0.08)] border-[rgba(0,184,255,0.2)]'}`}>
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--oh-text-secondary)] mb-1">Resolution</p>
                  <p className={`font-heading text-4xl font-bold ${market.resolution === 'yes' ? 'text-[#00FFA3]' : 'text-[#00B8FF]'}`}>
                    {market.resolution?.toUpperCase()}
                  </p>
                </div>
                <p className="text-xs text-[var(--oh-text-muted)] text-center mt-4">
                  This market has been resolved. No further trades can be placed.
                </p>
              </div>
            ) : (
              /* Active Market - Trade Panel */
              <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-6 sticky top-24 animate-fade-up" style={{ animationDelay: '0.15s' }} data-testid="trade-panel">
                <h3 className="font-heading text-lg text-[var(--oh-text)] mb-5">Place Your Trade</h3>

                {/* Amount Input */}
                <div className="mb-5">
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--oh-text-secondary)] mb-2 block">Amount ($)</label>
                  <Input type="number" value={tradeAmount} onChange={e => setTradeAmount(e.target.value)}
                    placeholder="Enter amount" min="1" step="0.01"
                    className="bg-[var(--oh-bg)] border-[var(--oh-border)] focus:border-[#00FFA3] focus:ring-1 focus:ring-[#00FFA3] text-[var(--oh-text)] placeholder-[var(--oh-text-muted)] h-12 font-mono text-lg"
                    data-testid="trade-amount-input" />
                  <div className="flex gap-2 mt-2">
                    {[10, 50, 100, 500].map(amt => (
                      <button key={amt} onClick={() => setTradeAmount(String(amt))}
                        className="flex-1 py-1.5 text-xs font-mono text-[var(--oh-text-secondary)] bg-[var(--oh-bg)] border border-[var(--oh-border)] rounded hover:border-[#00FFA3] hover:text-[#00FFA3] transition-colors"
                        data-testid={`quick-amount-${amt}`}>
                        ${amt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Trade Preview */}
                {amount > 0 && (
                  <div className="mb-5 space-y-3">
                    <div className="bg-[rgba(0,255,163,0.05)] rounded-lg p-3 border border-[rgba(0,255,163,0.1)]">
                      <div className="flex justify-between text-xs text-[var(--oh-text-secondary)] mb-1">
                        <span>YES Shares</span>
                        <span className="font-mono text-[#00FFA3]">{yesShares}</span>
                      </div>
                      <div className="flex justify-between text-xs text-[var(--oh-text-secondary)]">
                        <span>Potential Payout</span>
                        <span className="font-mono text-[#00FFA3]">${yesPayout}</span>
                      </div>
                    </div>
                    <div className="bg-[rgba(0,184,255,0.05)] rounded-lg p-3 border border-[rgba(0,184,255,0.1)]">
                      <div className="flex justify-between text-xs text-[var(--oh-text-secondary)] mb-1">
                        <span>NO Shares</span>
                        <span className="font-mono text-[#00B8FF]">{noShares}</span>
                      </div>
                      <div className="flex justify-between text-xs text-[var(--oh-text-secondary)]">
                        <span>Potential Payout</span>
                        <span className="font-mono text-[#00B8FF]">${noPayout}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Trade Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <Button onClick={() => handleTrade('yes')} disabled={trading || !amount}
                    className="w-full bg-[#00FFA3] text-black font-bold text-lg py-6 rounded-lg border-2 border-transparent hover:shadow-[0_0_20px_rgba(0,255,163,0.4)] transition-all"
                    data-testid="trade-yes-button">
                    YES
                    <ArrowUpRight size={18} className="ml-1" />
                  </Button>
                  <Button onClick={() => handleTrade('no')} disabled={trading || !amount}
                    className="w-full bg-[#00B8FF] text-black font-bold text-lg py-6 rounded-lg border-2 border-transparent hover:shadow-[0_0_20px_rgba(0,184,255,0.4)] transition-all"
                    data-testid="trade-no-button">
                    NO
                    <ArrowUpRight size={18} className="ml-1" />
                  </Button>
                </div>

                <p className="text-xs text-[var(--oh-text-muted)] text-center mt-4">
                  Virtual currency. Simulated trading for educational purposes.
                </p>
              </div>
            )}

            {/* Admin Resolve Panel */}
            {user?.role === 'admin' && market.status === 'active' && (
              <div className="bg-[var(--oh-card)] border border-[#F59E0B]/30 rounded-xl p-6 animate-fade-up" style={{ animationDelay: '0.2s' }} data-testid="admin-resolve-panel">
                <div className="flex items-center gap-2 mb-4">
                  <Gavel size={20} className="text-[#F59E0B]" weight="fill" />
                  <h3 className="font-heading text-base text-[var(--oh-text)]">Admin: Resolve Market</h3>
                </div>
                <p className="text-xs text-[var(--oh-text-muted)] mb-4">Resolving distributes payouts to all traders. This action is permanent.</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button onClick={() => handleResolve('yes')} disabled={resolving}
                    className="w-full bg-[#00FFA3] text-black font-bold py-4 rounded-lg hover:shadow-[0_0_20px_rgba(0,255,163,0.4)] transition-all"
                    data-testid="admin-resolve-yes">
                    Resolve YES
                  </Button>
                  <Button onClick={() => handleResolve('no')} disabled={resolving}
                    className="w-full bg-[#00B8FF] text-black font-bold py-4 rounded-lg hover:shadow-[0_0_20px_rgba(0,184,255,0.4)] transition-all"
                    data-testid="admin-resolve-no">
                    Resolve NO
                  </Button>
                </div>
              </div>
            )}

            {/* Tags */}
            {market.tags?.length > 0 && (
              <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-5">
                <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--oh-text-secondary)] mb-3">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {market.tags.map(tag => (
                    <span key={tag} className="text-xs text-[var(--oh-text-secondary)] bg-[var(--oh-bg)] border border-[var(--oh-border)] px-3 py-1 rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
